import React from 'react';
import { MultiValue } from 'react-select';
import { Client, Scheme } from '@/types/client';
import {
  FormField,
  FormFieldNote,
  FormReactMultiSelect,
  SelectOption,
  SelectOptionGroup,
} from '@/components/utils/FormCard';

interface OrderClientSelectorProps {
  schemes: Scheme[];
  /** Schemes whose clients are offered, one option group each. */
  schemeIds: number[];
  schemeClients: Record<number, Client[]>;
  value: number[];
  onChange: (clientIds: number[]) => void;
  loading?: boolean;
  required?: boolean;
  hint?: React.ReactNode;
  error?: string;
  disabled?: boolean;
}

const clientLabel = (c: Client) =>
  `${c.client_code} - ${c.full_name || `${c.first_name} ${c.last_name}`.trim()}`;

/** Multi-select of clients grouped by scheme, shared by the buy and sell order forms. */
const OrderClientSelector: React.FC<OrderClientSelectorProps> = ({
  schemes,
  schemeIds,
  schemeClients,
  value,
  onChange,
  loading = false,
  required = false,
  hint,
  error,
  disabled = false,
}) => {
  const groupedOptions: SelectOptionGroup[] = schemeIds.map(schemeId => ({
    label: schemes.find(s => s.id === schemeId)?.code || `Scheme ${schemeId}`,
    options: (schemeClients[schemeId] || []).map(c => ({ value: c.id, label: clientLabel(c) })),
  }));
  const selectedOptions = groupedOptions
    .flatMap(g => g.options)
    .filter(o => value.includes(o.value));

  return (
    <FormField label="Clients" required={required} error={error} hint={hint}>
      {loading ? (
        <FormFieldNote>Loading clients…</FormFieldNote>
      ) : schemeIds.length === 0 ? (
        <FormFieldNote>Select a scheme to load clients.</FormFieldNote>
      ) : (
        <FormReactMultiSelect
          options={groupedOptions}
          value={selectedOptions}
          onChange={(selected: MultiValue<SelectOption>) => onChange(selected ? selected.map(o => o.value) : [])}
          placeholder={required ? 'Select clients…' : 'All eligible clients'}
          hasError={Boolean(error)}
          isDisabled={disabled}
        />
      )}
    </FormField>
  );
};

export default OrderClientSelector;
